/**
 * Pairwise Coverage Matrix
 *
 * Displays two heatmap views of pairwise vote coverage:
 * 1. Vote counts: Shows how many preference votes for each model pair
 *    - Cell (row, col) shows votes FOR the column model when row vs col competed
 * 2. User coverage: Shows how many unique users voted on each pair (symmetric)
 */

import { useEffect, useMemo, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

// =============================================================================
// Types
// =============================================================================

interface PairwiseModelInfo {
  configId: string;
  modelName: string;
  modelKey: string;
}

interface PairwiseVoteCount {
  modelAId: string;
  modelBId: string;
  votesForA: number;
  votesForB: number;
  ties: number;
  bothBad: number;
  totalVotes: number;
  uniqueUsers: number;
}

interface PairwiseCoverageData {
  models: PairwiseModelInfo[];
  pairwiseCounts: PairwiseVoteCount[];
  totals: {
    totalVotes: number;
    totalPairsWithVotes: number;
    totalPossiblePairs: number;
  };
}

type ViewMode = 'votes' | 'users';

interface CellData {
  value: number;
  displayValue: string;
  pair: PairwiseVoteCount | null;
  isDiagonal: boolean;
}

// =============================================================================
// Utility Functions
// =============================================================================

function getHeatmapColor(
  value: number,
  maxValue: number,
  mode: ViewMode
): string {
  if (value === 0) return '#F5F3F0';
  const intensity = Math.min(value / Math.max(maxValue, 1), 1);

  if (mode === 'votes') {
    // Red palette
    const r = Math.round(255 - intensity * 116);
    const g = Math.round(243 - intensity * 208);
    const b = Math.round(240 - intensity * 190);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Blue palette
    const r = Math.round(255 - intensity * 218);
    const g = Math.round(243 - intensity * 144);
    const b = Math.round(240 - intensity * 5);
    return `rgb(${r}, ${g}, ${b})`;
  }
}

// =============================================================================
// Sub-Components
// =============================================================================

function ViewModeToggle({
  mode,
  onChange,
}: {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}) {
  return (
    <div className="flex gap-1 bg-[#F5F3F0] rounded-lg p-1">
      <button
        onClick={() => onChange('votes')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          mode === 'votes'
            ? 'bg-white shadow-sm text-[#8B2332]'
            : 'text-[#6B6560] hover:text-[#1A1A1A]'
        }`}
      >
        Vote Counts
      </button>
      <button
        onClick={() => onChange('users')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          mode === 'users'
            ? 'bg-white shadow-sm text-[#2563EB]'
            : 'text-[#6B6560] hover:text-[#1A1A1A]'
        }`}
      >
        Unique Users
      </button>
    </div>
  );
}

function HeatmapLegend({
  maxValue,
  mode,
}: {
  maxValue: number;
  mode: ViewMode;
}) {
  const steps = 5;
  const values = Array.from({ length: steps + 1 }, (_, i) =>
    Math.round((maxValue * i) / steps)
  );

  return (
    <div className="flex items-center gap-2 text-xs text-[#6B6560]">
      <span>0</span>
      <div className="flex h-3">
        {values.slice(0, -1).map((v, i) => (
          <div
            key={i}
            className="w-6 h-full"
            style={{ backgroundColor: getHeatmapColor(v, maxValue, mode) }}
          />
        ))}
      </div>
      <span>{maxValue}</span>
    </div>
  );
}

function SummaryStats({ totals }: { totals: PairwiseCoverageData['totals'] }) {
  const coveragePercent = Math.round(
    (totals.totalPairsWithVotes / Math.max(totals.totalPossiblePairs, 1)) * 100
  );

  return (
    <div className="flex gap-6 text-sm">
      <div>
        <span className="text-[#6B6560]">Total Votes:</span>{' '}
        <span className="font-semibold">
          {totals.totalVotes.toLocaleString()}
        </span>
      </div>
      <div>
        <span className="text-[#6B6560]">Pairs with Votes:</span>{' '}
        <span className="font-semibold">
          {totals.totalPairsWithVotes} / {totals.totalPossiblePairs}
        </span>{' '}
        <span className="text-[#6B6560]">({coveragePercent}%)</span>
      </div>
    </div>
  );
}

function HoverTooltip({
  rowModel,
  colModel,
  pair,
  viewMode,
}: {
  rowModel: PairwiseModelInfo;
  colModel: PairwiseModelInfo;
  pair: PairwiseVoteCount;
  viewMode: ViewMode;
}) {
  if (viewMode === 'votes') {
    return (
      <span>
        <strong>{rowModel.modelName}</strong> vs{' '}
        <strong>{colModel.modelName}</strong>: {pair.votesForA} for row,{' '}
        {pair.votesForB} for column, {pair.ties} ties, {pair.bothBad} both bad
      </span>
    );
  }
  return (
    <span>
      <strong>{rowModel.modelName}</strong> vs{' '}
      <strong>{colModel.modelName}</strong>: {pair.uniqueUsers} unique users
    </span>
  );
}

function MatrixCell({
  cellData,
  maxValue,
  viewMode,
  isHovered,
  isLowerTriangle,
  onMouseEnter,
  onMouseLeave,
  title,
}: {
  cellData: CellData;
  maxValue: number;
  viewMode: ViewMode;
  isHovered: boolean;
  isLowerTriangle: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  title: string;
}) {
  const baseStyle = {
    minWidth: '42px',
    height: '32px',
  };

  if (isLowerTriangle) {
    return (
      <td
        className="p-1 text-center border border-[#E8E3DD]"
        style={{ ...baseStyle, backgroundColor: '#FAFAF8' }}
      />
    );
  }

  const { value, displayValue, isDiagonal } = cellData;

  return (
    <td
      className={`p-1 text-center border border-[#E8E3DD] cursor-default transition-all ${
        isHovered ? 'ring-2 ring-[#8B2332] ring-inset' : ''
      }`}
      style={{
        ...baseStyle,
        backgroundColor: isDiagonal
          ? '#E8E3DD'
          : getHeatmapColor(value, maxValue, viewMode),
        color: isDiagonal
          ? '#6B6560'
          : value > maxValue * 0.6
            ? '#FFFFFF'
            : '#1A1A1A',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      title={title}
    >
      {displayValue}
    </td>
  );
}

function MatrixTable({
  models,
  cellMatrix,
  maxValue,
  viewMode,
  hoveredCell,
  onCellHover,
}: {
  models: PairwiseModelInfo[];
  cellMatrix: CellData[][];
  maxValue: number;
  viewMode: ViewMode;
  hoveredCell: { row: number; col: number } | null;
  onCellHover: (cell: { row: number; col: number } | null) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-xs">
        <thead>
          <tr>
            <th className="p-1 text-right text-[#6B6560] font-normal min-w-[80px]">
              Row ↓ \\ Col →
            </th>
            {models.map(model => (
              <th
                key={model.configId}
                className="p-1 text-center font-medium text-[#1A1A1A] max-w-[100px] overflow-hidden"
                style={{
                  writingMode: 'vertical-lr',
                  transform: 'rotate(180deg)',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={model.modelName}
              >
                {model.modelName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {models.map((rowModel, rowIdx) => (
            <tr key={rowModel.configId}>
              <td
                className="p-1 text-right text-[#1A1A1A] font-medium pr-2 max-w-[120px] overflow-hidden whitespace-nowrap"
                style={{ textOverflow: 'ellipsis' }}
                title={rowModel.modelName}
              >
                {rowModel.modelName}
              </td>
              {models.map((colModel, colIdx) => {
                const cellData = cellMatrix[rowIdx][colIdx];
                const isLowerTriangle = viewMode === 'users' && rowIdx > colIdx;
                const isHovered =
                  hoveredCell?.row === rowIdx && hoveredCell?.col === colIdx;

                return (
                  <MatrixCell
                    key={colModel.configId}
                    cellData={cellData}
                    maxValue={maxValue}
                    viewMode={viewMode}
                    isHovered={isHovered}
                    isLowerTriangle={isLowerTriangle}
                    onMouseEnter={() =>
                      !cellData.isDiagonal &&
                      onCellHover({ row: rowIdx, col: colIdx })
                    }
                    onMouseLeave={() => onCellHover(null)}
                    title={
                      cellData.isDiagonal
                        ? rowModel.modelName
                        : `${rowModel.modelName} vs ${colModel.modelName}`
                    }
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatrixHeader({
  viewMode,
  onViewModeChange,
}: {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold text-[#1A1A1A]">
          Pairwise Vote Coverage Matrix
        </h3>
        <p className="text-sm text-[#6B6560] mt-1 min-h-[40px]">
          {viewMode === 'votes'
            ? 'Cell shows votes FOR the column model when that pair competed. Read as: "In Row vs Column, Column received X votes."'
            : 'Upper triangle shows unique users per model pair (symmetric, so only one triangle needed).'}
        </p>
      </div>
      <div className="flex-shrink-0">
        <ViewModeToggle mode={viewMode} onChange={onViewModeChange} />
      </div>
    </div>
  );
}

function MatrixFooter({ viewMode }: { viewMode: ViewMode }) {
  return (
    <div className="text-xs text-[#6B6560] pt-2 border-t border-[#E8E3DD]">
      {viewMode === 'votes' ? (
        <>
          <strong>Reading the matrix:</strong> Each cell shows votes{' '}
          <em>for the column model</em> when that row-vs-column pair competed.
          The matrix is <em>not symmetric</em> — flipping row/column shows votes
          for the other model in the same matchup.
        </>
      ) : (
        <>
          <strong>Reading the matrix:</strong> Upper triangle shows unique users
          per pair. Since it&apos;s symmetric (same users vote on both sides),
          only one triangle is shown.
        </>
      )}
    </div>
  );
}

// =============================================================================
// Loading / Error / Empty States
// =============================================================================

function LoadingState() {
  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-6">
      <div className="text-center text-[#6B6560] py-8">
        Loading pairwise coverage data...
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-6">
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {message}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-6">
      <div className="text-center text-[#6B6560] py-8">
        No pairwise vote data available
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function PairwiseCoverageMatrix() {
  const [data, setData] = useState<PairwiseCoverageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('votes');
  const [hoveredCell, setHoveredCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  // Fetch data
  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const response = await apiRequest('/api/admin/pairwise-coverage');
        setData(response as PairwiseCoverageData);
      } catch (e) {
        logError(
          'Failed to load pairwise coverage',
          e instanceof Error ? e : undefined
        );
        setError(
          e instanceof Error ? e.message : 'Failed to load pairwise coverage'
        );
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Precompute all cell data and max values
  const { cellMatrix, maxVotes, maxUsers } = useMemo(() => {
    if (!data) {
      return { cellMatrix: [], maxVotes: 0, maxUsers: 0 };
    }

    // Build lookup map for pairs
    const pairMap = new Map<string, PairwiseVoteCount>();
    for (const pair of data.pairwiseCounts) {
      pairMap.set(`${pair.modelAId}-${pair.modelBId}`, pair);
      // Store flipped version
      pairMap.set(`${pair.modelBId}-${pair.modelAId}`, {
        ...pair,
        modelAId: pair.modelBId,
        modelBId: pair.modelAId,
        votesForA: pair.votesForB,
        votesForB: pair.votesForA,
      });
    }

    // Precompute matrix and find max values
    let maxVotes = 0;
    let maxUsers = 0;

    const cellMatrix: CellData[][] = data.models.map((rowModel, rowIdx) =>
      data.models.map((colModel, colIdx) => {
        const isDiagonal = rowIdx === colIdx;

        if (isDiagonal) {
          return { value: -1, displayValue: '—', pair: null, isDiagonal: true };
        }

        const pair = pairMap.get(`${rowModel.configId}-${colModel.configId}`);

        if (!pair) {
          return { value: 0, displayValue: '0', pair: null, isDiagonal: false };
        }

        // Track max for heatmap color scaling
        maxVotes = Math.max(maxVotes, pair.votesForA, pair.votesForB);
        maxUsers = Math.max(maxUsers, pair.uniqueUsers);

        return {
          value: pair.votesForB,
          displayValue: pair.votesForB.toString(),
          pair,
          isDiagonal: false,
        };
      })
    );

    return { cellMatrix, maxVotes, maxUsers };
  }, [data]);

  const { displayMatrix, maxValue } = useMemo(() => {
    if (viewMode === 'users') {
      const displayMatrix = cellMatrix.map(row =>
        row.map(cell => ({
          ...cell,
          value: cell.pair?.uniqueUsers ?? cell.value,
          displayValue: cell.pair?.uniqueUsers?.toString() ?? cell.displayValue,
        }))
      );
      return { displayMatrix, maxValue: maxUsers };
    }
    return { displayMatrix: cellMatrix, maxValue: maxVotes };
  }, [cellMatrix, viewMode, maxVotes, maxUsers]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data || data.models.length === 0) return <EmptyState />;

  const hoveredPair =
    hoveredCell && displayMatrix[hoveredCell.row]?.[hoveredCell.col]?.pair;

  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-6 space-y-4">
      <MatrixHeader viewMode={viewMode} onViewModeChange={setViewMode} />

      <SummaryStats totals={data.totals} />

      <div className="flex items-center justify-between">
        <HeatmapLegend maxValue={maxValue} mode={viewMode} />
        {hoveredCell && hoveredPair && (
          <div className="text-sm text-[#6B6560]">
            <HoverTooltip
              rowModel={data.models[hoveredCell.row]}
              colModel={data.models[hoveredCell.col]}
              pair={hoveredPair}
              viewMode={viewMode}
            />
          </div>
        )}
      </div>

      <MatrixTable
        models={data.models}
        cellMatrix={displayMatrix}
        maxValue={maxValue}
        viewMode={viewMode}
        hoveredCell={hoveredCell}
        onCellHover={setHoveredCell}
      />

      <MatrixFooter viewMode={viewMode} />
    </div>
  );
}
